import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Box,
  Alert,
  Chip,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material';
import {
  Print as PrintIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { treatmentAPI, sessionAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 900,
  margin: '0 auto',
  padding: theme.spacing(2),
}));

const PatientInfoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.grey[50],
  border: `1px solid ${theme.palette.grey[200]}`,
}));

const TreatmentDetailsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const SignatureSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  paddingTop: theme.spacing(3),
  borderTop: `1px solid ${theme.palette.grey[300]}`,
}));

function TreatmentDetails() {
  const { treatmentId } = useParams();
  const navigate = useNavigate();
  const { isDoctor } = useAuth();
  const [treatment, setTreatment] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completingTreatment, setCompletingTreatment] = useState(false);
  const [success, setSuccess] = useState('');
  const [completeFormData, setCompleteFormData] = useState({
    performedBy: '',
    description: '',
    nextAppointment: null,
    duration: ''
  });

  useEffect(() => {
    if (treatmentId) {
      fetchTreatmentDetails();
      fetchSessions();
    } else {
      setError('No treatment ID provided');
      setLoading(false);
    }
  }, [treatmentId]);

  const fetchTreatmentDetails = async () => {
    try {
      const response = await treatmentAPI.getById(treatmentId);
      const treatmentData = response.data.data;
      setTreatment(treatmentData);

      // duration in completion form
      setCompleteFormData(prev => ({
        ...prev,
        duration: treatmentData.duration || ''
      }));

      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch treatment details');
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await sessionAPI.getByTreatmentId(treatmentId);
      setSessions(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleCompleteChange = (e) => {
    setCompleteFormData({
      ...completeFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleNextAppointmentChange = (newValue) => {
    setCompleteFormData({
      ...completeFormData,
      nextAppointment: newValue
    });
  };

  const handleCompleteTreatment = async (e) => {
    e.preventDefault();
    setCompletingTreatment(true);
    setError('');
    setSuccess('');

    try {
      const submitData = {
        ...completeFormData,
        nextAppointment: completeFormData.nextAppointment?.toISOString() || null
      };
      const response = await treatmentAPI.complete(treatmentId, submitData);
      setTreatment(response.data.data);
      setSuccess(response.data.message);
      setShowCompleteModal(false);
      setCompleteFormData({
        performedBy: '',
        description: '',
        nextAppointment: null,
        duration: ''
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to complete treatment');
    } finally {
      setCompletingTreatment(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'In Progress':
        return 'info';
      case 'Scheduled':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !treatment) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!treatment) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Treatment not found</Alert>
      </Box>
    );
  }

  const patient = treatment.patient;

  if (!patient) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Patient information not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <StyledCard>
        <CardContent>
          {/* Action Buttons */}
          <Box className="no-print" sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {isDoctor() && treatment.status !== 'Completed' && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/session/new/${treatmentId}`)}
                >
                  Add Session
                </Button>
              )}
              {treatment.status === 'Scheduled' && isDoctor() && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => setShowCompleteModal(true)}
                >
                  Complete Treatment Plan
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
              >
                Print
              </Button>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(`/patient/${patient?.id || treatment.patientId}`)}
              >
                Back to Patient
              </Button>
            </Box>
            
            {success && (
              <Alert severity="success" sx={{ mt: 2 }}>
                {success}
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>

          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h3" component="h1" gutterBottom>
              Treatment Plan Record
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {formatDate(treatment.treatmentDate)}
            </Typography>
          </Box>

          {/* Patient Information */}
          <PatientInfoCard>
            <Typography variant="h5" gutterBottom>
              👤 Patient Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography><strong>Name:</strong> {patient?.firstName || 'N/A'} {patient?.lastName || ''}</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography><strong>Age:</strong> {patient?.dateOfBirth ? calculateAge(patient.dateOfBirth) : 'N/A'} years</Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography><strong>Gender:</strong> {patient?.gender || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography><strong>Phone:</strong> {patient?.phone || 'N/A'}</Typography>
              </Grid>
              {patient?.allergies && (
                <Grid item xs={12}>
                  <Chip
                    label={`⚠️ Allergies: ${patient.allergies}`}
                    color="error"
                    variant="outlined"
                    sx={{ fontWeight: 'bold', mt: 1 }}
                  />
                </Grid>
              )}
            </Grid>
          </PatientInfoCard>

          {/* Treatment Plan Details */}
          <TreatmentDetailsCard>
            <Typography variant="h5" gutterBottom>
              🩺 Treatment Plan Details
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Typography variant="h6" color="primary" gutterBottom>
                  {treatment.treatmentType}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Chip
                  label={treatment.status}
                  color={getStatusColor(treatment.status)}
                  icon={treatment.status === 'Scheduled' ? <ScheduleIcon /> : <CheckCircleIcon />}
                  sx={{ fontWeight: 'bold' }}
                />
              </Grid>

              {treatment.description && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Description:</Typography>
                  <Typography>{treatment.description}</Typography>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Performed By:</Typography>
                <Typography>{treatment.performedBy || 'To be assigned'}</Typography>
              </Grid>

              {treatment.duration && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Duration:</Typography>
                  <Typography>{treatment.duration}</Typography>
                </Grid>
              )}

              {treatment.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Additional Notes:</Typography>
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>{treatment.notes}</Typography>
                </Grid>
              )}

              {treatment.nextAppointment && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Next Appointment:</Typography>
                  <Typography>{formatDate(treatment.nextAppointment)}</Typography>
                </Grid>
              )}
            </Grid>
          </TreatmentDetailsCard>

          {/* Sessions Section */}
          {sessions.length > 0 && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h5" gutterBottom>
                📋 Treatment Sessions
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Session #</strong></TableCell>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Cost</strong></TableCell>
                      <TableCell><strong>Medicines</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sessions.map((session) => (
                      <TableRow
                        key={session.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/session/${session.id}`)}
                      >
                        <TableCell>{session.sessionNumber}</TableCell>
                        <TableCell>{formatDate(session.sessionDate)}</TableCell>
                        <TableCell>
                          {session.cost ? `₹${parseFloat(session.cost).toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell>
                          {session.prescribedMedicines?.length || 0} medicine(s)
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/session/${session.id}`);
                            }}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Signature Section */}
          <SignatureSection>
            <Grid container justifyContent="flex-end">
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ textAlign: 'right', mb: 2 }}>
                  Signature: _______________________
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
                  {treatment.performedBy || 'To be completed'}
                </Typography>
              </Grid>
            </Grid>
          </SignatureSection>
        </CardContent>
      </StyledCard>

      {/* Complete Treatment Modal */}
      <Dialog
        open={showCompleteModal}
        onClose={() => !completingTreatment && setShowCompleteModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleCompleteTreatment}>
          <DialogTitle>
            Complete Treatment Plan
          </DialogTitle>
          <DialogContent>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Complete the <strong>{treatment.treatmentType}</strong> treatment plan
            </Typography>

            <TextField
              fullWidth
              label="Performed By *"
              name="performedBy"
              value={completeFormData.performedBy}
              onChange={handleCompleteChange}
              placeholder="Name of doctor/staff who performed the treatment"
              required
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              label="Treatment Plan Description"
              name="description"
              value={completeFormData.description}
              onChange={handleCompleteChange}
              multiline
              rows={3}
              placeholder="Describe what was done during the treatment plan..."
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              label="Duration"
              name="duration"
              value={completeFormData.duration}
              onChange={handleCompleteChange}
              placeholder="e.g., 30 minutes"
              helperText="Update treatment plan duration if needed"
              sx={{ mb: 3 }}
            />

            <DateTimePicker
              label="Next Appointment (Optional)"
              value={completeFormData.nextAppointment}
              onChange={handleNextAppointmentChange}
              sx={{ width: '100%' }}
              slotProps={{
                textField: {
                  helperText: 'If provided, a new treatment will be automatically scheduled'
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowCompleteModal(false);
                setCompleteFormData({
                  performedBy: '',
                  description: '',
                  nextAppointment: null,
                  duration: treatment.duration || ''
                });
              }}
              disabled={completingTreatment}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={completingTreatment}
              startIcon={completingTreatment && <CircularProgress size={16} />}
            >
              {completingTreatment ? 'Completing...' : 'Complete Treatment Plan'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default TreatmentDetails;